import type { CognitoUserPool } from 'amazon-cognito-identity-js'

/**
 * Configuration options for the API client
 */
export interface ApiClientConfig {
  baseUrl: string
  timeout?: number
  userPool: CognitoUserPool
  onUnauthorized?: () => void
}

/**
 * Unified API client using fetch and Cognito SDK for authentication
 *
 * This client is shared between member and admin portals to ensure consistent
 * authentication, error handling, and API communication patterns.
 */
export class ApiClient {
  private baseUrl: string
  private timeout: number
  private userPool: CognitoUserPool
  private onUnauthorized?: () => void

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl
    this.timeout = config.timeout || 10000 // 10 seconds default
    this.userPool = config.userPool
    this.onUnauthorized = config.onUnauthorized
  }

  /**
   * Get authentication headers with ID token from Cognito session
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const cognitoUser = this.userPool.getCurrentUser()

    if (!cognitoUser) {
      // No user logged in - return headers without auth
      return {
        'Content-Type': 'application/json',
      }
    }

    return new Promise((resolve) => {
      cognitoUser.getSession((err: Error | null, session: unknown) => {
        if (err || !session) {
          // Session error - return headers without auth
          resolve({
            'Content-Type': 'application/json',
          })
          return
        }

        // Type assertion for Cognito session
        const cognitoSession = session as {
          isValid: () => boolean
          getIdToken: () => { getJwtToken: () => string }
        }

        if (!cognitoSession.isValid()) {
          // Invalid session - return headers without auth
          resolve({
            'Content-Type': 'application/json',
          })
          return
        }

        // Send ID token — required by REST API COGNITO_USER_POOLS authorizer
        // which validates the aud claim (present in ID tokens, not access tokens)
        const idToken = cognitoSession.getIdToken().getJwtToken()

        resolve({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        })
      })
    })
  }

  /**
   * Handle response and errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle 401 Unauthorized - auto logout
    if (response.status === 401) {
      const cognitoUser = this.userPool.getCurrentUser()
      if (cognitoUser) {
        cognitoUser.signOut()
      }

      // Call custom unauthorized handler if provided
      if (this.onUnauthorized) {
        this.onUnauthorized()
      }

      throw new Error('Unauthorized - session expired')
    }

    // Handle other errors
    if (!response.ok) {
      let errorMessage = 'An error occurred'
      try {
        const errorData = (await response.json()) as {
          message?: string
          error?: string
        }
        errorMessage = errorData.message || errorData.error || errorMessage
      } catch {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage
      }
      throw new Error(errorMessage)
    }

    return response.json()
  }

  /**
   * Make a fetch request with timeout
   */
  private async fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      return response
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - please try again')
      }
      if (error instanceof TypeError) {
        throw new Error('No response from server. Please check your connection.')
      }
      throw error
    }
  }

  async get<T>(path: string, options?: { params?: Record<string, string | number> }): Promise<T> {
    let url = `${this.baseUrl}${path}`

    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        searchParams.append(key, String(value))
      })
      url += `?${searchParams.toString()}`
    }

    const headers = await this.getAuthHeaders()

    const response = await this.fetchWithTimeout(url, {
      method: 'GET',
      headers,
    })

    return this.handleResponse<T>(response)
  }

  async post<T>(path: string, data: unknown): Promise<T> {
    const headers = await this.getAuthHeaders()

    const response = await this.fetchWithTimeout(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    })

    return this.handleResponse<T>(response)
  }

  async put<T>(path: string, data: unknown): Promise<T> {
    const headers = await this.getAuthHeaders()

    const response = await this.fetchWithTimeout(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    })

    return this.handleResponse<T>(response)
  }

  async delete<T>(path: string): Promise<T> {
    const headers = await this.getAuthHeaders()

    const response = await this.fetchWithTimeout(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers,
    })

    return this.handleResponse<T>(response)
  }
}
